<template>
  <div style="background-color:#4a5568; min-height:100vh; padding: 20px;">
    <div class="container">
      <div class="header">
        <v-btn class="back-button" @click="confirmBackNavigation">
          <v-icon left>mdi-arrow-left</v-icon>Back
        </v-btn>
        <h1 class="title">Edit Sample #{{ sampleId }}</h1>
      </div>

      <v-card class="form-card" elevation="5">
        <v-overlay :value="loading" absolute>
          <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>

        <v-form ref="form" v-model="valid" lazy-validation>
          <div class="form-grid">
            <!-- LEFT COLUMN -->
            <div class="primary-info">
              <h2 class="section-header">PRIMARY INFORMATION</h2>

              <div class="form-group">
                <label class="field-label">Sample ID</label>
                <v-text-field v-model="sampleId" outlined dense readonly hide-details></v-text-field>
              </div>

              <div class="form-group">
                <label class="field-label">Sample Status <span class="required">*</span></label>
                <v-select
                  v-model="sampleStatus"
                  :items="statusOptions"
                  outlined dense hide-details
                  @change="onSampleStatusChange"
                  :rules="[rules.required]"
                ></v-select>
              </div>

              <div class="form-group">
                <label class="field-label">Customer <span class="required">*</span></label>
                <v-autocomplete
                  v-model="selectedCustomer"
                  :items="filteredCustomers"
                  :search-input.sync="customerSearch"
                  item-text="companyName"
                  item-value="docId"
                  outlined dense hide-details
                  placeholder="Type 3+ characters to search..."
                  :rules="[rules.required]"
                  return-object
                  no-data-text="No customers found"
                  :no-filter="true"
                ></v-autocomplete>
              </div>

              <div class="form-group">
                <label class="field-label">Contact Person <span class="required">*</span></label>
                <v-text-field v-model="contactPerson" outlined dense hide-details :rules="[rules.required]"></v-text-field>
              </div>

              <div class="form-group">
                <label class="field-label">Reference Number <span class="required">*</span></label>
                <v-text-field v-model="referenceNumber" outlined dense hide-details :rules="[rules.required]"></v-text-field>
              </div>

              <div class="form-group">
                <label class="field-label">Bin Number <span class="required">*</span></label>
                <v-select
                  v-model="binNumber"
                  :items="availableBins"
                  outlined dense hide-details
                  placeholder="Select Bin..."
                  :rules="[rules.required]"
                ></v-select>
              </div>
            </div>

            <!-- RIGHT COLUMN -->
            <div class="secondary-info">
              <h2 class="section-header">ASSIGNMENT & DATES</h2>

              <div class="form-group">
                <label class="field-label">Received By <span class="required">*</span></label>
                <v-select
                  v-model="receivedBy"
                  :items="receivedByOptions"
                  outlined dense hide-details
                  :rules="[rules.required]"
                ></v-select>
              </div>

              <div class="form-group">
                <label class="field-label">Assigned To</label>
                <v-select
                  v-model="assignedTo"
                  :items="assignedToOptions"
                  outlined dense hide-details
                  :rules="[rules.assignedRequired]"
                  :disabled="assignedToOptions.length === 0"
                ></v-select>
              </div>

              <div class="form-group">
                <label class="field-label">Date Received</label>
                <v-text-field v-model="createdDate" outlined dense type="date" hide-details></v-text-field>
              </div>

              <div class="form-group">
                <label class="field-label">Date Returned</label>
                <v-menu
                  v-if="showReturnDate"
                  ref="returnDateMenu"
                  v-model="returnDateMenu"
                  :close-on-content-click="false"
                  transition="scale-transition"
                  offset-y
                  min-width="290px"
                >
                  <template v-slot:activator="{ on, attrs }">
                    <v-text-field v-model="returnDate" outlined dense hide-details v-bind="attrs" v-on="on" :rules="[rules.returnDateRequired]"></v-text-field>
                  </template>
                  <v-date-picker v-model="returnDate" @input="returnDateMenu = false"></v-date-picker>
                </v-menu>
                <v-text-field v-else outlined dense hide-details disabled></v-text-field>
              </div>

              <div class="form-group">
                <label class="field-label">Quote Number</label>
                <v-text-field
                  v-if="showQuoteNumber"
                  v-model="quoteNumber"
                  outlined dense hide-details
                  :rules="[rules.quoteRequired]"
                ></v-text-field>
                <v-text-field v-else outlined dense hide-details disabled></v-text-field>
              </div>
            </div>
          </div>

          <!-- Sample Information -->
          <div class="full-width-section">
            <h2 class="section-header">SAMPLE INFORMATION</h2>
            <div class="form-group">
              <label class="field-label">Description <span class="required">*</span></label>
              <v-textarea v-model="sampleInformation" outlined rows="4" auto-grow hide-details :rules="[rules.required]"></v-textarea>
            </div>
          </div>

          <!-- Sample Items -->
          <div class="full-width-section">
            <h2 class="section-header">SAMPLE ITEMS</h2>
            <div class="sample-items-container">
              <div v-for="(item, index) in sampleItems" :key="index" class="sample-item-card">
                <div class="sample-item-header">
                  <span class="item-number">Item #{{ index + 1 }}</span>
                  <div class="item-actions">
                    <v-btn icon small class="icon-btn camera" @click="triggerFileInput(index)">
                      <v-icon small>mdi-camera</v-icon>
                    </v-btn>
                    <input :ref="'fileInput' + index" type="file" accept="image/*" style="display:none" multiple @change="handleFileUpload($event, index)" />
                    <v-btn v-if="item.pictures && item.pictures.length > 0" icon small class="icon-btn view" @click="openViewImagesDialog(item.pictures)">
                      <v-icon small>mdi-eye</v-icon>
                    </v-btn>
                    <v-btn icon small class="icon-btn delete" @click="confirmRemoveItem(index)">
                      <v-icon small>mdi-close</v-icon>
                    </v-btn>
                  </div>
                </div>
                <div class="photo-badge" v-if="item.pictures && item.pictures.length > 0">
                  {{ item.pictures.length }} Photo{{ item.pictures.length !== 1 ? 's' : '' }}
                </div>
                <div class="item-grid">
                  <div class="item-form-group">
                    <label class="item-label">QTY <span class="required">*</span></label>
                    <v-text-field v-model="item.qty" outlined dense hide-details :rules="[rules.required, rules.numeric]"></v-text-field>
                  </div>
                  <div class="item-form-group">
                    <label class="item-label">Code <span class="required">*</span></label>
                    <v-text-field v-model="item.code" outlined dense hide-details :rules="[rules.required]"></v-text-field>
                  </div>
                  <div class="item-form-group">
                    <label class="item-label">Description <span class="required">*</span></label>
                    <v-text-field v-model="item.description" outlined dense hide-details :rules="[rules.required]"></v-text-field>
                  </div>
                  <div class="item-form-group">
                    <label class="item-label">Stat OD/Housing <span class="required">*</span></label>
                    <v-text-field v-model="item.statOdHousing" outlined dense hide-details :rules="[rules.required]"></v-text-field>
                  </div>
                  <div class="item-form-group">
                    <label class="item-label">Competitor Code</label>
                    <v-text-field v-model="item.competitorCode" outlined dense hide-details></v-text-field>
                  </div>
                  <div class="item-form-group">
                    <label class="item-label">Brand & Pump Model</label>
                    <v-text-field v-model="item.brandPumpModel" outlined dense hide-details></v-text-field>
                  </div>
                  <div class="item-form-group">
                    <label class="item-label">Elastomer</label>
                    <v-text-field v-model="item.elastomer" outlined dense hide-details></v-text-field>
                  </div>
                  <div class="item-form-group">
                    <label class="item-label">Face Materials</label>
                    <v-text-field v-model="item.faceMaterials" outlined dense hide-details></v-text-field>
                  </div>
                  <div class="item-form-group">
                    <label class="item-label">Product</label>
                    <v-text-field v-model="item.product" outlined dense hide-details></v-text-field>
                  </div>
                  <div class="item-form-group">
                    <label class="item-label">Pressure</label>
                    <v-text-field v-model="item.pressure" outlined dense hide-details :rules="[rules.numericOptional]"></v-text-field>
                  </div>
                  <div class="item-form-group">
                    <label class="item-label">Temperature</label>
                    <v-text-field v-model="item.temperature" outlined dense hide-details :rules="[rules.numericOptional]"></v-text-field>
                  </div>
                </div>
              </div>
            </div>
            <v-btn class="btn-secondary" @click="confirmAddItem">
              <v-icon left>mdi-plus</v-icon>Add Another Item
            </v-btn>
          </div>

          <div class="actions-row">
            <v-btn class="btn-primary" large @click="confirmUpdate">
              <v-icon left>mdi-content-save</v-icon>Update Sample
            </v-btn>
          </div>
        </v-form>
      </v-card>
    </div>

    <v-dialog v-model="confirmationDialog.visible" max-width="500">
      <v-card>
        <v-card-title class="dialog-title">{{ confirmationDialog.title }}</v-card-title>
        <v-card-text class="pa-6">{{ confirmationDialog.message }}</v-card-text>
        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn text @click="confirmationDialog.visible = false">No</v-btn>
          <v-btn color="#4a5568" dark @click="executeConfirm">Yes</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="snackbar" :color="snackbarColor" timeout="3000">
      {{ snackbarMessage }}
      <v-btn color="white" text @click="snackbar = false">Close</v-btn>
    </v-snackbar>

    <v-snackbar v-model="photoSnackbar" color="success" timeout="3000">
      {{ photoSnackbarMessage }}
      <v-btn color="white" text @click="photoSnackbar = false">Close</v-btn>
    </v-snackbar>

    <v-dialog v-model="viewImagesDialog.visible" max-width="600">
      <v-card>
        <v-card-title class="dialog-title">Uploaded Photos</v-card-title>
        <v-card-text>
          <v-carousel hide-delimiter-background>
            <v-carousel-item v-for="(image, i) in viewImagesDialog.images" :key="i">
              <v-img :src="image" aspect-ratio="1.75"></v-img>
            </v-carousel-item>
          </v-carousel>
        </v-card-text>
        <v-card-actions class="pa-6">
          <v-spacer></v-spacer>
          <v-btn color="#4a5568" dark @click="viewImagesDialog.visible = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import {
  getFirestore, collection, getDocs, doc, getDoc, updateDoc,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { getAuth } from "firebase/auth";
import { firebaseApp } from "@/firebaseConfig";

const db = getFirestore(firebaseApp);

const STATUS_OPTIONS = [
  "Booked", "Inspection", "Drawing", "Pending Quote",
  "Quoted", "To be Returned", "At Dispatch", "Return Completed",
];

const BIN_CLEAR_STATUSES = ["At Dispatch", "Return Completed"];

const STATUS_ASSIGNED_KEY = {
  "Booked":            "samplesBooked",
  "Inspection":        "samplesInspection",
  "Drawing":           "samplesDrawing",
  "Pending Quote":     "samplesPendingQuote",
  "Quoted":            "samplesQuoted",
  "To be Returned":    "samplesToBeReturned",
  "At Dispatch":       "samplesAtDispatch",
  "Return Completed":  "samplesReturnCompleted",
};

export default {
  data() {
    return {
      docId: "",
      referenceNumber: "",
      loading: false,
      valid: false,
      sampleId: "",
      sampleStatus: "Booked",
      previousStatus: "Booked",
      customers: [],
      customerSearch: "",
      selectedCustomer: null,
      receivedBy: null,
      assignedTo: null,
      contactPerson: "",
      returnDate: "",
      createdDate: "",
      returnDateMenu: false,
      receivedByOptions: [],
      assignedToOptions: [],
      allSampleDropdowns: {},
      sampleItems: [],
      confirmationDialog: { visible: false, title: "", message: "", confirm: null },
      snackbar: false,
      snackbarMessage: "",
      snackbarColor: "success",
      photoSnackbar: false,
      photoSnackbarMessage: "",
      viewImagesDialog: { visible: false, images: [] },
      statusOptions: STATUS_OPTIONS,
      rules: {
        required: (value) => !!value || "Required.",
        numeric: (value) => {
          if (value === "") return "Required.";
          return !isNaN(value) || "Must be numeric.";
        },
        numericOptional: (value) => {
          if (value === "") return true;
          return !isNaN(value) || "Must be numeric.";
        },
        assignedRequired: (value) => {
          if (this.sampleStatus !== "Booked" && this.sampleStatus !== "Inspection") {
            return !!value || "Assigned To is required for this status.";
          }
          return true;
        },
        returnDateRequired: (value) => {
          if (this.showReturnDate) return !!value || "Date Returned is required.";
          return true;
        },
        quoteRequired: (value) => {
          if (this.showQuoteNumber) return !!value || "Quote Number is required.";
          return true;
        },
      },
      sampleInformation: "",
      userPermissions: {},
      availableBins: [],
      totalBins: Array.from({ length: 40 }, (_, i) => (i + 1).toString()),
      takenBins: [],
      binNumber: null,
      quoteNumber: "",
      userRole: "",
    };
  },
  computed: {
    canFreelyChangeStatus() {
      return this.userRole === "Admin" || this.userPermissions.Override === true;
    },
    showReturnDate() {
      return ["To be Returned", "At Dispatch", "Return Completed"].includes(this.sampleStatus);
    },
    showQuoteNumber() {
      return ["Pending Quote", "Quoted"].includes(this.sampleStatus);
    },
    filteredCustomers() {
      if (this.customerSearch && typeof this.customerSearch === 'string' && this.customerSearch.length >= 3) {
        const s = this.customerSearch.toLowerCase();
        return this.customers.filter(c => c.companyName.toLowerCase().includes(s));
      }
      return [];
    },
  },
  async created() {
    this.loading = true;
    this.docId = this.$route.params.id;
    await this.fetchSampleDropdowns();
    await this.fetchCustomers();
    await this.fetchBins();
    await this.loadSampleData();
    await this.fetchBins(); // refresh after load to include current bin

    const auth = getAuth(firebaseApp);
    const currentUser = auth.currentUser;
    if (currentUser) {
      try {
        const userDoc = await getDoc(doc(db, "users", currentUser.uid));
        if (userDoc.exists()) {
          this.userPermissions = userDoc.data().samples || {};
          this.userRole = userDoc.data().role || "";
        }
      } catch (error) {
        console.error("Error fetching user permissions:", error);
      }
    }
    this.loading = false;
  },
  methods: {
    blankItem() {
      return {
        qty: "", code: "", description: "", pictures: [],
        competitorCode: "", brandPumpModel: "", elastomer: "",
        faceMaterials: "", product: "", pressure: "", temperature: "", statOdHousing: "",
      };
    },

    async fetchSampleDropdowns() {
      try {
        const ddDoc = await getDoc(doc(db, "admin", "dropdownManager"));
        if (ddDoc.exists()) {
          this.allSampleDropdowns = ddDoc.data();
          this.receivedByOptions = ddDoc.data().samplesReceivedBy || [];
        }
      } catch (error) {
        console.error("Error fetching sample dropdowns:", error);
      }
    },

    updateAssignedToOptions() {
      const key = STATUS_ASSIGNED_KEY[this.sampleStatus];
      this.assignedToOptions = key ? (this.allSampleDropdowns[key] || []) : [];
      if (this.assignedTo && !this.assignedToOptions.includes(this.assignedTo)) {
        this.assignedTo = null;
      }
    },

    async loadSampleData() {
      try {
        const sampleDoc = await getDoc(doc(db, "samples", this.docId));
        if (sampleDoc.exists()) {
          const data = sampleDoc.data();
          this.sampleId = data.sampleId || "";
          this.sampleStatus = data.sampleStatus || "Booked";
          this.previousStatus = this.sampleStatus;
          this.referenceNumber = data.referenceNumber || "";
          this.contactPerson = data.contactPerson || "";
          this.sampleInformation = data.sampleInformation || "";
          this.returnDate = data.returnDate || "";
          this.createdDate = data.createdDate || "";
          this.binNumber = data.binNumber || null;
          this.quoteNumber = data.quoteNumber || "";
          this.receivedBy = typeof data.receivedBy === 'object' && data.receivedBy !== null ? data.receivedBy.name : (data.receivedBy || null);
          this.assignedTo = typeof data.assignedTo === 'object' && data.assignedTo !== null ? data.assignedTo.name : (data.assignedTo || null);

          if (data.customer?.docId) {
            this.selectedCustomer = this.customers.find(c => c.docId === data.customer.docId) || null;
            // Set search text so the autocomplete shows the current value
            if (this.selectedCustomer) {
              this.customerSearch = this.selectedCustomer.companyName;
            }
          }

          this.sampleItems = (data.sampleItems || []).map(item => ({
            qty: item.qty || "", code: item.code || "", description: item.description || "",
            pictures: item.pictures || [], competitorCode: item.competitorCode || "",
            brandPumpModel: item.brandPumpModel || "", elastomer: item.elastomer || "",
            faceMaterials: item.faceMaterials || "", product: item.product || "",
            pressure: item.pressure || "", temperature: item.temperature || "",
            statOdHousing: item.statOdHousing || "",
          }));

          this.updateAssignedToOptions();
        }
      } catch (error) {
        console.error("Error loading sample data:", error);
        this.snackbar = true;
        this.snackbarMessage = "Error loading sample data.";
        this.snackbarColor = "error";
      }
    },

    async fetchBins() {
      try {
        const snap = await getDocs(collection(db, "samples"));
        this.takenBins = snap.docs
          .filter(d => {
            const data = d.data();
            return data.binNumber != null &&
              !BIN_CLEAR_STATUSES.includes(data.sampleStatus) &&
              d.id !== this.docId;
          })
          .map(d => d.data().binNumber.toString());

        this.availableBins = this.totalBins.filter(b => !this.takenBins.includes(b));

        // Ensure current bin is always in the list
        if (this.binNumber && !this.availableBins.includes(this.binNumber.toString())) {
          this.availableBins.push(this.binNumber.toString());
          this.availableBins.sort((a, b) => parseInt(a) - parseInt(b));
        }
      } catch (error) {
        console.error("Error fetching bins:", error);
        this.snackbar = true;
        this.snackbarMessage = "Error loading bins. Please refresh.";
        this.snackbarColor = "error";
      }
    },

    onSampleStatusChange(newStatus) {
      if (!this.canFreelyChangeStatus) {
        const prevIndex = STATUS_OPTIONS.indexOf(this.previousStatus);
        const newIndex = STATUS_OPTIONS.indexOf(newStatus);
        if (prevIndex === -1 || newIndex === -1 || newIndex < prevIndex || newIndex > prevIndex + 1) {
          this.sampleStatus = this.previousStatus;
          this.snackbar = true;
          this.snackbarMessage = "You are not allowed to change the status out of sequence.";
          this.snackbarColor = "error";
          return;
        }
      }
      this.sampleStatus = newStatus;
      if (!this.showReturnDate) this.returnDate = "";
      if (!this.showQuoteNumber) this.quoteNumber = "";
      this.updateAssignedToOptions();
      if (this.$refs.form) this.$nextTick(() => this.$refs.form.resetValidation());
      this.previousStatus = this.sampleStatus;
    },

    async fetchCustomers() {
      try {
        const snap = await getDocs(collection(db, "customers"));
        this.customers = snap.docs.map(d => ({
          docId: d.id,
          companyName: d.data()["Company Name"] || "",
          contactPerson: d.data()["Contact Person"] || "",
          customerAccount: d.data()["Customer Account"] || "",
        })).sort((a, b) => a.companyName.localeCompare(b.companyName));
      } catch (error) {
        console.error("Error fetching customers:", error);
      }
    },

    triggerFileInput(index) {
      this.$refs[`fileInput${index}`][0].click();
    },

    async handleFileUpload(event, index) {
      const files = event.target.files;
      const storage = getStorage(firebaseApp);
      for (let file of files) {
        try {
          const storageRef = ref(storage, `samples/${this.sampleId}/${file.name}`);
          await uploadBytes(storageRef, file);
          const url = await getDownloadURL(storageRef);
          this.sampleItems[index].pictures.push(url);
        } catch (error) {
          console.error("Error uploading file:", error);
        }
      }
      this.photoSnackbarMessage = "Photo uploaded successfully!";
      this.photoSnackbar = true;
    },

    confirmAddItem() {
      this.confirmationDialog = {
        visible: true, title: "Confirm Action",
        message: "Are you sure you want to add a new sample item?",
        confirm: this.addMoreSampleItems,
      };
    },

    addMoreSampleItems() {
      this.confirmationDialog.visible = false;
      this.sampleItems.push(this.blankItem());
    },

    confirmRemoveItem(index) {
      this.confirmationDialog = {
        visible: true, title: "Confirm Deletion",
        message: "Are you sure you want to remove this sample item?",
        confirm: () => this.removeSampleItem(index),
      };
    },
    removeSampleItem(index) {
      this.confirmationDialog.visible = false;
      this.sampleItems.splice(index, 1);
    },

    confirmUpdate() {
      if (this.$refs.form.validate()) {
        this.confirmationDialog = {
          visible: true, title: "Confirm Update",
          message: "Are you sure you want to update this sample?",
          confirm: () => this.updateSample(),
        };
      } else {
        this.snackbar = true;
        this.snackbarMessage = "Please fill in all required fields.";
        this.snackbarColor = "error";
      }
    },

    executeConfirm() {
      if (this.confirmationDialog.confirm) this.confirmationDialog.confirm();
      this.confirmationDialog.visible = false;
    },

    async updateSample() {
      this.loading = true;
      try {
        const clearBin = BIN_CLEAR_STATUSES.includes(this.sampleStatus);
        const sampleData = {
          sampleId: this.sampleId,
          sampleStatus: this.sampleStatus,
          quoteNumber: this.showQuoteNumber ? this.quoteNumber : "",
          receivedBy: this.receivedBy || null,
          assignedTo: this.assignedTo || null,
          customer: this.selectedCustomer
            ? {
                docId: this.selectedCustomer.docId,
                companyName: this.selectedCustomer.companyName,
                contactPerson: this.selectedCustomer.contactPerson,
                customerAccount: this.selectedCustomer.customerAccount,
              }
            : {},
          contactPerson: this.contactPerson,
          referenceNumber: this.referenceNumber,
          sampleInformation: this.sampleInformation,
          returnDate: this.showReturnDate ? this.returnDate : "",
          createdDate: this.createdDate,
          binNumber: clearBin ? null : (this.binNumber ? parseInt(this.binNumber, 10) : null),
          sampleItems: this.sampleItems.map(item => ({ ...item })),
        };

        await updateDoc(doc(db, "samples", this.docId), sampleData);

        this.snackbar = true;
        this.snackbarMessage = "Sample updated successfully!";
        this.snackbarColor = "success";
        setTimeout(() => this.$router.go(-1), 1500);
      } catch (error) {
        console.error("Error updating sample:", error);
        this.snackbar = true;
        this.snackbarMessage = "Error updating sample.";
        this.snackbarColor = "error";
      } finally {
        this.loading = false;
      }
    },

    confirmBackNavigation() {
      this.confirmationDialog = {
        visible: true, title: "Confirm Navigation",
        message: "Are you sure you want to go back? Unsaved changes will be lost.",
        confirm: this.goBack,
      };
    },

    goBack() {
      this.confirmationDialog.visible = false;
      this.$router.go(-1);
    },
    openViewImagesDialog(images) {
      this.viewImagesDialog.images = images;
      this.viewImagesDialog.visible = true;
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700&display=swap");
* { font-family: "Montserrat", sans-serif; }
.container { max-width: 1400px; margin: 0 auto; }
.header { margin-bottom: 30px; }
.back-button { background: rgba(255,255,255,0.1) !important; border: 1px solid rgba(255,255,255,0.2); color: white !important; border-radius: 12px; margin-bottom: 20px; text-transform: none; }
.back-button:hover { background: rgba(255,255,255,0.2) !important; transform: scale(1.05); }
.title { color: white; font-size: 1.8rem; font-weight: 700; text-align: center; }
.form-card { background: white; border-radius: 20px; padding: 40px; }
.form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 30px; margin-bottom: 40px; }
.primary-info { border-right: 2px solid #e5e7eb; padding-right: 30px; }
.secondary-info { padding-left: 30px; }
.section-header { color: #2e3a59; font-weight: 700; font-size: 0.9rem; text-transform: uppercase; letter-spacing: 1.5px; margin-bottom: 20px; padding-bottom: 10px; border-bottom: 3px solid #4a5568; text-shadow: 1px 1px 2px rgba(0,0,0,0.1); }
.form-group { margin-bottom: 20px; }
.field-label { display: block; font-size: 0.875rem; font-weight: 600; color: #374151; margin-bottom: 6px; }
.required { color: #ef4444; }
.full-width-section { margin-top: 40px; padding-top: 40px; border-top: 3px solid #e5e7eb; }
.sample-items-container { margin-top: 30px; }
.sample-item-card { background: #f9fafb; border: 2px solid #e5e7eb; border-radius: 12px; padding: 25px; margin-bottom: 20px; position: relative; transition: all 0.3s; }
.sample-item-card:hover { box-shadow: 0 4px 12px rgba(0,0,0,0.08); border-color: #4a5568; }
.sample-item-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; padding-bottom: 15px; border-bottom: 2px solid #e5e7eb; }
.item-number { font-weight: 700; color: #4a5568; font-size: 1rem; }
.item-actions { display: flex; gap: 10px; }
.icon-btn { width: 36px; height: 36px; border-radius: 8px; border: 2px solid #e5e7eb; }
.icon-btn.camera { border-color: #4a5568; color: #4a5568; }
.icon-btn.view { border-color: #3b82f6; color: #3b82f6; }
.icon-btn.delete { border-color: #ef4444; color: #ef4444; }
.photo-badge { position: absolute; top: 10px; right: 10px; background: #10b981; color: white; padding: 4px 12px; border-radius: 20px; font-size: 0.75rem; font-weight: 600; }
.item-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 15px; }
.item-form-group { margin-bottom: 0; }
.item-label { display: block; font-size: 0.8rem; font-weight: 600; color: #374151; margin-bottom: 4px; }
.btn-secondary { background: white !important; color: #4a5568 !important; border: 2px solid #4a5568 !important; text-transform: none; font-weight: 600; border-radius: 10px; }
.btn-secondary:hover { background: #4a5568 !important; color: white !important; }
.actions-row { display: flex; gap: 15px; justify-content: center; margin-top: 40px; padding-top: 30px; border-top: 2px solid #e5e7eb; }
.btn-primary { background: #4a5568 !important; color: white !important; text-transform: none; font-weight: 600; border-radius: 10px; }
.btn-primary:hover { background: #374151 !important; transform: translateY(-2px); box-shadow: 0 6px 20px rgba(74,85,104,0.3); }
.dialog-title { background: #4a5568; color: white; }
@media (max-width: 1024px) {
  .form-grid { grid-template-columns: 1fr; }
  .primary-info { border-right: none; border-bottom: 2px solid #e5e7eb; padding-right: 0; padding-bottom: 30px; }
  .secondary-info { padding-left: 0; padding-top: 10px; }
  .item-grid { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 640px) {
  .item-grid { grid-template-columns: 1fr; }
  .form-card { padding: 20px; }
  .actions-row { flex-direction: column; }
}
</style>